import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useLocale, useSetLocale, useTranslations } from 'gt-react'
import { Globe } from 'lucide-react'
import { cn } from '@/lib/utils'

const LOCALES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
] as const

interface LanguageSwitcherProps {
  className?: string
}

/**
 * Globe icon → dropdown of available locales. Flipping the locale
 * through `useSetLocale` re-runs `loadDictionary` inside GTProvider
 * and re-renders every consumer — no full page reload needed.
 */
export function LanguageSwitcher({ className }: LanguageSwitcherProps) {
  const locale = useLocale()
  const setLocale = useSetLocale()
  const t = useTranslations('language')

  const active = LOCALES.find((l) => l.code === locale) ?? LOCALES[0]

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={t('label')}
          className={cn(
            'inline-flex h-9 items-center gap-2 rounded-full border border-border',
            'bg-background/60 px-3 text-sm backdrop-blur',
            'hover:bg-accent hover:text-accent-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'transition-colors',
            className,
          )}
        >
          <Globe className="h-4 w-4" />
          <span className="hidden sm:inline">{active.code.toUpperCase()}</span>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          className={cn(
            'z-50 min-w-[8rem] overflow-hidden rounded-lg border border-border',
            'bg-popover p-1 text-popover-foreground shadow-lg',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          )}
        >
          {LOCALES.map((l) => (
            <DropdownMenu.Item
              key={l.code}
              onSelect={() => setLocale(l.code)}
              className={cn(
                'relative flex cursor-pointer select-none items-center gap-2 rounded-md',
                'px-2 py-1.5 text-sm outline-none',
                'hover:bg-accent hover:text-accent-foreground',
                'data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground',
                l.code === locale && 'font-semibold',
              )}
            >
              <span aria-hidden="true">{l.flag}</span>
              <span>{l.label}</span>
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
